import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner, Badge } from 'react-bootstrap';
import * as pdfjs from 'pdfjs-dist';
import { PDFDocument, rgb } from 'pdf-lib';
import SignaturePad from '../SignaturePad';
import './PDFSignatureEditor.scss';
import { getBaseUrl } from '../../app/services/EndPoints';
import { getFetchDefaultHeaders } from '../../app/services/shared/vars';

// Configure PDF.js worker
if (typeof window !== 'undefined') {
    pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

interface SignaturePlacement {
    id: string;
    x: number; // percentage from left
    y: number; // percentage from top
    width: number; // percentage
    height: number; // percentage
    page: number;
    signatureDataUrl: string;
    type: 'signature' | 'date' | 'name';
}

interface PDFSignatureEditorProps {
    pdfUrl: string;
    documentTitle?: string;
    userName?: string;
    onSign: (signedPdfBlob: Blob, signatures: SignaturePlacement[]) => void;
    onCancel: () => void;
    isVisible: boolean;
}

const PDFSignatureEditor: React.FC<PDFSignatureEditorProps> = ({
    pdfUrl,
    documentTitle = 'Documento',
    userName = '',
    onSign,
    onCancel,
    isVisible
}) => {
    const [pdfDoc, setPdfDoc] = useState<pdfjs.PDFDocumentProxy | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [zoom, setZoom] = useState(100);
    const [pageImages, setPageImages] = useState<Map<number, string>>(new Map());
    const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);

    // Signature state
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [currentSignature, setCurrentSignature] = useState<string | null>(null);
    const [signatures, setSignatures] = useState<SignaturePlacement[]>([]);
    const [isPlacingSignature, setIsPlacingSignature] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [draggedSignature, setDraggedSignature] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Resize state
    const [isResizing, setIsResizing] = useState(false);
    const [resizingSignature, setResizingSignature] = useState<string | null>(null);
    const [resizeCorner, setResizeCorner] = useState<'nw' | 'ne' | 'sw' | 'se' | null>(null);
    const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

    const pageRef = useRef<HTMLDivElement>(null);

    // Load PDF using pdfjs-dist
    useEffect(() => {
        if (!pdfUrl || !isVisible) return;

        const loadPdf = async () => {
            try {
                setIsLoading(true);
                setLoadingProgress(10);

                // Fetch PDF. Si viene de nuestra API va con la sesión (los PDFs del
                // trámite exigen token); a Spaces nunca se le manda el token.
                let isApiUrl = false;
                try {
                    isApiUrl = new URL(pdfUrl, window.location.href).origin === new URL(getBaseUrl()).origin;
                } catch {
                    isApiUrl = false;
                }
                const response = await fetch(pdfUrl, isApiUrl ? { headers: getFetchDefaultHeaders() } : undefined);
                if (!response.ok) {
                    throw new Error(`Failed to fetch PDF: ${response.status}`);
                }

                const arrayBuffer = await response.arrayBuffer();
                
                // Make a copy of the ArrayBuffer for later use (pdf-lib)
                const pdfBytesCopy = arrayBuffer.slice(0);
                setPdfBytes(pdfBytesCopy);
                setLoadingProgress(30);

                // Load with PDF.js using another copy
                const loadingTask = pdfjs.getDocument({ data: arrayBuffer.slice(0) });
                const pdf = await loadingTask.promise;
                setPdfDoc(pdf);
                setTotalPages(pdf.numPages);
                setLoadingProgress(50);

                // Render all pages as images
                const images = new Map<number, string>();
                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    const scale = 2; // High resolution
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d')!;
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    await page.render({ canvasContext: context, viewport, canvas }).promise;
                    images.set(i, canvas.toDataURL('image/png'));
                    setLoadingProgress(50 + Math.floor((i / pdf.numPages) * 50));
                }

                setPageImages(images);
                setIsLoading(false);

            } catch (error) {
                console.error('Error loading PDF:', error);
                setIsLoading(false);
            }
        };

        loadPdf();

        return () => {
            // Cleanup blob URLs
            pageImages.forEach((url) => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [pdfUrl, isVisible]);

    // Handle signature creation
    const handleSignatureCreate = (signatureDataUrl: string) => {
        setCurrentSignature(signatureDataUrl);
        setShowSignaturePad(false);
        setIsPlacingSignature(true);
    };

    // Handle page click for placing signature
    const handlePageClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isPlacingSignature || !currentSignature || !pageRef.current) return;

        const rect = pageRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        const signatureWidth = 20;
        const signatureHeight = 10;

        const newSignature: SignaturePlacement = {
            id: `sig-${Date.now()}`,
            x: Math.max(0, Math.min(100 - signatureWidth, x - signatureWidth / 2)),
            y: Math.max(0, Math.min(100 - signatureHeight, y - signatureHeight / 2)),
            width: signatureWidth,
            height: signatureHeight,
            page: currentPage,
            signatureDataUrl: currentSignature,
            type: 'signature',
        };

        setSignatures([...signatures, newSignature]);
        setIsPlacingSignature(false);
        setCurrentSignature(null);
    };

    // Handle signature drag
    const handleSignatureDragStart = (e: React.MouseEvent, signatureId: string) => {
        e.stopPropagation();
        setIsDragging(true);
        setDraggedSignature(signatureId);
    };

    const handleSignatureDrag = useCallback((e: MouseEvent) => {
        if (!isDragging || !draggedSignature || !pageRef.current) return;

        const rect = pageRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        setSignatures((prev) =>
            prev.map((sig) =>
                sig.id === draggedSignature
                    ? {
                          ...sig,
                          x: Math.max(0, Math.min(100 - sig.width, x - sig.width / 2)),
                          y: Math.max(0, Math.min(100 - sig.height, y - sig.height / 2)),
                      }
                    : sig
            )
        );
    }, [isDragging, draggedSignature]);

    const handleSignatureDragEnd = useCallback(() => {
        setIsDragging(false);
        setDraggedSignature(null);
    }, []);

    // Handle resize
    const handleResizeStart = (e: React.MouseEvent, signatureId: string, corner: 'nw' | 'ne' | 'sw' | 'se') => {
        e.stopPropagation();
        e.preventDefault();
        
        const sig = signatures.find(s => s.id === signatureId);
        if (!sig) return;
        
        setIsResizing(true);
        setResizingSignature(signatureId);
        setResizeCorner(corner);
        setResizeStart({ x: sig.x, y: sig.y, width: sig.width, height: sig.height });
    };

    const handleResizeDrag = useCallback((e: MouseEvent) => {
        if (!isResizing || !resizingSignature || !pageRef.current || !resizeStart || !resizeCorner) return;

        const rect = pageRef.current.getBoundingClientRect();
        const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
        const mouseY = ((e.clientY - rect.top) / rect.height) * 100;

        const aspectRatio = resizeStart.width / resizeStart.height;

        setSignatures((prev) =>
            prev.map((sig) => {
                if (sig.id !== resizingSignature) return sig;

                let newX = sig.x;
                let newY = sig.y;
                let newWidth = sig.width;
                let newHeight = sig.height;
                const minSize = 5;

                if (resizeCorner === 'se') {
                    newWidth = Math.max(minSize, mouseX - resizeStart.x);
                    newHeight = newWidth / aspectRatio;
                } else if (resizeCorner === 'sw') {
                    newWidth = Math.max(minSize, resizeStart.x + resizeStart.width - mouseX);
                    newHeight = newWidth / aspectRatio;
                    newX = Math.min(mouseX, resizeStart.x + resizeStart.width - minSize);
                } else if (resizeCorner === 'ne') {
                    newWidth = Math.max(minSize, mouseX - resizeStart.x);
                    newHeight = newWidth / aspectRatio;
                    newY = resizeStart.y + resizeStart.height - newHeight;
                } else if (resizeCorner === 'nw') {
                    newWidth = Math.max(minSize, resizeStart.x + resizeStart.width - mouseX);
                    newHeight = newWidth / aspectRatio;
                    newX = Math.min(mouseX, resizeStart.x + resizeStart.width - minSize);
                    newY = resizeStart.y + resizeStart.height - newHeight;
                }

                if (newHeight < minSize) {
                    newHeight = minSize;
                    newWidth = newHeight * aspectRatio;
                }

                newX = Math.max(0, Math.min(100 - newWidth, newX));
                newY = Math.max(0, Math.min(100 - newHeight, newY));
                newWidth = Math.min(newWidth, 100 - newX);
                newHeight = Math.min(newHeight, 100 - newY);

                return { ...sig, x: newX, y: newY, width: newWidth, height: newHeight };
            })
        );
    }, [isResizing, resizingSignature, resizeStart, resizeCorner]);

    const handleResizeEnd = useCallback(() => {
        setIsResizing(false);
        setResizingSignature(null);
        setResizeCorner(null);
        setResizeStart(null);
    }, []);

    // Mouse event listeners
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleSignatureDrag);
            window.addEventListener('mouseup', handleSignatureDragEnd);
            return () => {
                window.removeEventListener('mousemove', handleSignatureDrag);
                window.removeEventListener('mouseup', handleSignatureDragEnd);
            };
        }
        if (isResizing) {
            window.addEventListener('mousemove', handleResizeDrag);
            window.addEventListener('mouseup', handleResizeEnd);
            return () => {
                window.removeEventListener('mousemove', handleResizeDrag);
                window.removeEventListener('mouseup', handleResizeEnd);
            };
        }
    }, [isDragging, isResizing, handleSignatureDrag, handleSignatureDragEnd, handleResizeDrag, handleResizeEnd]);

    const handleDeleteSignature = (signatureId: string) => {
        setSignatures(signatures.filter((s) => s.id !== signatureId));
    };

    const handleAddDate = () => {
        const newDate: SignaturePlacement = {
            id: `date-${Date.now()}`,
            x: 10,
            y: 85,
            width: 15,
            height: 4,
            page: currentPage,
            signatureDataUrl: '',
            type: 'date',
        };
        setSignatures([...signatures, newDate]);
    };

    const handleAddName = () => {
        const newName: SignaturePlacement = {
            id: `name-${Date.now()}`,
            x: 10,
            y: 78,
            width: 25,
            height: 4,
            page: currentPage,
            signatureDataUrl: '',
            type: 'name',
        };
        setSignatures([...signatures, newName]);
    };

    // Generate signed PDF using pdf-lib to embed signatures
    const handleGenerateSignedPdf = async () => {
        if (!pdfBytes || signatures.length === 0) {
            alert('Por favor, coloca al menos una firma en el documento');
            return;
        }

        setIsProcessing(true);

        try {
            // Load the PDF with pdf-lib
            const pdfDocLib = await PDFDocument.load(pdfBytes);
            const pages = pdfDocLib.getPages();

            // Process each signature
            for (const sig of signatures) {
                const pageIndex = sig.page - 1;
                if (pageIndex < 0 || pageIndex >= pages.length) continue;

                const page = pages[pageIndex];
                const { width: pageWidth, height: pageHeight } = page.getSize();

                // Calculate position (pdf-lib uses bottom-left origin)
                const x = (sig.x / 100) * pageWidth;
                const y = pageHeight - ((sig.y / 100) * pageHeight) - ((sig.height / 100) * pageHeight);
                const width = (sig.width / 100) * pageWidth;
                const height = (sig.height / 100) * pageHeight;

                if (sig.type === 'signature' && sig.signatureDataUrl) {
                    // Embed signature image
                    try {
                        const imageData = sig.signatureDataUrl;
                        let image;
                        
                        if (imageData.includes('image/png')) {
                            const base64Data = imageData.split(',')[1];
                            const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
                            image = await pdfDocLib.embedPng(imageBytes);
                        } else if (imageData.includes('image/jpeg') || imageData.includes('image/jpg')) {
                            const base64Data = imageData.split(',')[1];
                            const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
                            image = await pdfDocLib.embedJpg(imageBytes);
                        } else {
                            // Convert to PNG if format unknown
                            const canvas = document.createElement('canvas');
                            const img = new Image();
                            await new Promise<void>((resolve, reject) => {
                                img.onload = () => {
                                    canvas.width = img.width;
                                    canvas.height = img.height;
                                    const ctx = canvas.getContext('2d')!;
                                    ctx.drawImage(img, 0, 0);
                                    resolve();
                                };
                                img.onerror = reject;
                                img.src = imageData;
                            });
                            const pngData = canvas.toDataURL('image/png').split(',')[1];
                            const imageBytes = Uint8Array.from(atob(pngData), c => c.charCodeAt(0));
                            image = await pdfDocLib.embedPng(imageBytes);
                        }

                        const imgAspect = image.width / image.height;
                        const boxAspect = width / height;

                        let drawWidth = width;
                        let drawHeight = height;
                        let drawX = x;
                        let drawY = y;

                        if (imgAspect > boxAspect) {
                            drawHeight = width / imgAspect;
                            drawY = y + (height - drawHeight) / 2;
                        } else {
                            drawWidth = height * imgAspect;
                            drawX = x + (width - drawWidth) / 2;
                        }

                        page.drawImage(image, {
                            x: drawX,
                            y: drawY,
                            width: drawWidth,
                            height: drawHeight,
                        });
                    } catch (imgError) {
                        console.error('Error embedding signature image:', imgError);
                        throw new Error('No se pudo insertar la firma en el documento. Intenta nuevamente.');
                    }
                } else if (sig.type === 'date') {
                    // Draw date text
                    const dateText = new Date().toLocaleDateString('es-CL');
                    page.drawText(dateText, {
                        x,
                        y: y + height / 2,
                        size: 10,
                        color: rgb(0, 0, 0),
                    });
                } else if (sig.type === 'name') {
                    // Draw name text
                    page.drawText(userName, {
                        x,
                        y: y + height / 2,
                        size: 10,
                        color: rgb(0, 0, 0),
                    });
                }
            }

            // Save the modified PDF
            const modifiedPdfBytes = await pdfDocLib.save();
            const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });

            onSign(blob, signatures);

        } catch (error) {
            console.error('Error generating signed PDF:', error);
            alert('Error al generar el PDF firmado. Por favor, intenta de nuevo.');
        } finally {
            setIsProcessing(false);
        }
    };

    const currentPageImage = pageImages.get(currentPage);
    const currentPageSignatures = signatures.filter((s) => s.page === currentPage);

    return (
        <Modal 
            show={isVisible} 
            onHide={onCancel} 
            size="xl" 
            fullscreen="lg-down"
            className="pdf-signature-modal"
            backdrop="static"
        >
            <Modal.Header className="pdf-editor-header">
                <div className="header-info">
                    <Modal.Title>
                        <i className="fas fa-file-pdf me-2 text-danger"></i>
                        {documentTitle}
                    </Modal.Title>
                    <Badge bg="primary">Firma Digital</Badge>
                </div>
                <div className="header-actions">
                    <Button variant="outline-secondary" onClick={onCancel}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleGenerateSignedPdf}
                        disabled={signatures.length === 0 || isProcessing}
                    >
                        {isProcessing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Procesando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check-circle me-2"></i>
                                Firmar Documento
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Header>

            <Modal.Body className="pdf-editor-body p-0">
                <div className="pdf-editor-container">
                    {/* Left Sidebar - Tools */}
                    <div className="pdf-sidebar">
                        <div className="sidebar-section">
                            <h6 className="sidebar-title">
                                <i className="fas fa-pen-fancy me-2"></i>
                                Campos de Firma
                            </h6>
                            <div className="d-grid gap-2">
                                <Button 
                                    variant="outline-primary"
                                    onClick={() => setShowSignaturePad(true)}
                                >
                                    <i className="fas fa-signature me-2"></i>
                                    Agregar Firma
                                </Button>
                                <Button 
                                    variant="outline-secondary"
                                    onClick={handleAddName}
                                >
                                    <i className="fas fa-user me-2"></i>
                                    Agregar Nombre
                                </Button>
                                <Button 
                                    variant="outline-secondary"
                                    onClick={handleAddDate}
                                >
                                    <i className="fas fa-calendar me-2"></i>
                                    Agregar Fecha
                                </Button>
                            </div>
                        </div>

                        {/* Current Signature Preview */}
                        {isPlacingSignature && currentSignature && (
                            <div className="sidebar-section placing-alert">
                                <p className="text-primary mb-2">
                                    <i className="fas fa-crosshairs me-2"></i>
                                    Haz clic en el documento para colocar tu firma
                                </p>
                                <img 
                                    src={currentSignature} 
                                    alt="Tu firma" 
                                    className="signature-preview-small"
                                />
                                <Button 
                                    variant="link" 
                                    size="sm"
                                    className="text-muted"
                                    onClick={() => {
                                        setIsPlacingSignature(false);
                                        setCurrentSignature(null);
                                    }}
                                >
                                    <i className="fas fa-times me-2"></i>
                                    Cancelar
                                </Button>
                            </div>
                        )}

                        {/* Placed Signatures */}
                        {signatures.length > 0 && (
                            <div className="sidebar-section">
                                <h6 className="sidebar-title">
                                    <i className="fas fa-list me-2"></i>
                                    Campos Colocados ({signatures.length})
                                </h6>
                                <div className="placed-signatures-list">
                                    {signatures.map((sig) => (
                                        <div 
                                            key={sig.id} 
                                            className={`placed-signature-item ${sig.page === currentPage ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(sig.page)}
                                        >
                                            <div className="sig-info">
                                                {sig.type === 'signature' && <i className="fas fa-signature text-primary me-2"></i>}
                                                {sig.type === 'date' && <i className="fas fa-calendar text-success me-2"></i>}
                                                {sig.type === 'name' && <i className="fas fa-user text-info me-2"></i>}
                                                <span>
                                                    {sig.type === 'signature' && 'Firma'}
                                                    {sig.type === 'date' && 'Fecha'}
                                                    {sig.type === 'name' && 'Nombre'}
                                                </span>
                                                <Badge bg="secondary" className="ms-2">Pág. {sig.page}</Badge>
                                            </div>
                                            <Button 
                                                variant="link" 
                                                size="sm"
                                                className="text-danger p-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteSignature(sig.id);
                                                }}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Instructions */}
                        <div className="sidebar-section instructions">
                            <h6 className="sidebar-title">
                                <i className="fas fa-info-circle me-2"></i>
                                Instrucciones
                            </h6>
                            <ol className="instructions-list">
                                <li>Crea tu firma con &quot;Agregar Firma&quot;</li>
                                <li>Haz clic en el documento para colocarla</li>
                                <li>Arrastra para reposicionar</li>
                                <li>Haz clic en &quot;Firmar Documento&quot;</li>
                            </ol>
                        </div>
                    </div>

                    {/* Main PDF Viewer */}
                    <div className="pdf-viewer">
                        {/* Toolbar */}
                        <div className="pdf-toolbar">
                            <div className="zoom-controls">
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => setZoom(Math.max(50, zoom - 10))}
                                >
                                    <i className="fas fa-search-minus"></i>
                                </Button>
                                <span className="zoom-label">{zoom}%</span>
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => setZoom(Math.min(200, zoom + 10))}
                                >
                                    <i className="fas fa-search-plus"></i>
                                </Button>
                            </div>

                            {totalPages > 1 && (
                                <div className="page-controls">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage <= 1}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </Button>
                                    <span className="page-label">{currentPage} / {totalPages}</span>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage >= totalPages}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* PDF Page */}
                        <div className="pdf-page-container">
                            {isLoading ? (
                                <div className="loading-container">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3">Cargando documento... {loadingProgress}%</p>
                                    <div className="progress" style={{ width: '200px' }}>
                                        <div 
                                            className="progress-bar bg-primary" 
                                            style={{ width: `${loadingProgress}%` }}
                                        />
                                    </div>
                                </div>
                            ) : currentPageImage ? (
                                <div
                                    ref={pageRef}
                                    className={`pdf-page ${isPlacingSignature ? 'placing-mode' : ''}`}
                                    style={{ 
                                        transform: `scale(${zoom / 100})`,
                                        transformOrigin: 'top center'
                                    }}
                                    onClick={handlePageClick}
                                >
                                    {/* PDF page as image */}
                                    <img
                                        src={currentPageImage}
                                        alt={`Página ${currentPage}`}
                                        className="pdf-page-image"
                                        draggable={false}
                                    />

                                    {/* Render signatures */}
                                    {currentPageSignatures.map((sig) => (
                                        <div
                                            key={sig.id}
                                            className={`signature-overlay ${draggedSignature === sig.id || resizingSignature === sig.id ? 'dragging' : ''}`}
                                            style={{
                                                left: `${sig.x}%`,
                                                top: `${sig.y}%`,
                                                width: `${sig.width}%`,
                                                height: `${sig.height}%`,
                                            }}
                                            onMouseDown={(e) => handleSignatureDragStart(e, sig.id)}
                                        >
                                            {sig.type === 'signature' ? (
                                                <img
                                                    src={sig.signatureDataUrl}
                                                    alt="Firma"
                                                    className="signature-image"
                                                    draggable={false}
                                                />
                                            ) : sig.type === 'date' ? (
                                                <div className="date-field">
                                                    {new Date().toLocaleDateString('es-CL')}
                                                </div>
                                            ) : sig.type === 'name' ? (
                                                <div className="name-field">
                                                    {userName}
                                                </div>
                                            ) : null}

                                            {/* Resize handles */}
                                            <div
                                                className="resize-handle nw"
                                                onMouseDown={(e) => handleResizeStart(e, sig.id, 'nw')}
                                            />
                                            <div
                                                className="resize-handle ne"
                                                onMouseDown={(e) => handleResizeStart(e, sig.id, 'ne')}
                                            />
                                            <div
                                                className="resize-handle sw"
                                                onMouseDown={(e) => handleResizeStart(e, sig.id, 'sw')}
                                            />
                                            <div
                                                className="resize-handle se"
                                                onMouseDown={(e) => handleResizeStart(e, sig.id, 'se')}
                                            />

                                            {/* Toolbar on hover */}
                                            <div className="signature-toolbar">
                                                <i className="fas fa-grip-vertical me-2"></i>
                                                <span>Arrastra para mover</span>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="text-danger p-0 ms-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSignature(sig.id);
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Placing guide */}
                                    {isPlacingSignature && (
                                        <div className="placing-guide">
                                            <span>Haz clic para colocar la firma</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="error-container">
                                    <i className="fas fa-exclamation-circle fa-3x text-danger mb-3"></i>
                                    <p>No se pudo cargar el documento</p>
                                </div>
                            )}
                        </div>

                        {/* Page Thumbnails */}
                        {totalPages > 1 && (
                            <div className="pdf-thumbnails">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                    const pageHasSignatures = signatures.some((s) => s.page === page);
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`thumbnail-btn ${currentPage === page ? 'active' : ''}`}
                                        >
                                            {pageImages.get(page) && (
                                                <img
                                                    src={pageImages.get(page)}
                                                    alt={`Miniatura página ${page}`}
                                                    className="thumbnail-image"
                                                />
                                            )}
                                            <span className="thumbnail-number">{page}</span>
                                            {pageHasSignatures && (
                                                <div className="thumbnail-badge" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>

            {/* Signature Pad Modal */}
            <Modal 
                show={showSignaturePad} 
                onHide={() => setShowSignaturePad(false)}
                centered
                className="signature-pad-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-signature me-2 text-primary"></i>
                        Crear tu Firma
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SignaturePad
                        onSignatureCreate={handleSignatureCreate}
                        onClose={() => setShowSignaturePad(false)}
                        userName={userName}
                    />
                </Modal.Body>
            </Modal>
        </Modal>
    );
};

export default PDFSignatureEditor;
